import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { patchJoinPhotos } from '../lib/demoPhotos';

export function useVouches() {
  const { user } = useAuth();
  const userId = user?.id;
  const [pendingRequests, setPendingRequests] = useState([]);
  const [myVouches, setMyVouches] = useState([]);
  const [potentialJobsCount, setPotentialJobsCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Fetch job interests where the candidate is in this voucher's network
  // and the status is 'interested' (waiting for vouch)
  const fetchPendingRequests = useCallback(async () => {
    if (!userId) return;
    setLoading(true);

    // Get candidates connected to this voucher
    const { data: vouchConns } = await supabase
      .from('vouch_connections')
      .select('user_a_id, user_b_id')
      .or(`user_a_id.eq.${userId},user_b_id.eq.${userId}`);

    if (!vouchConns || vouchConns.length === 0) {
      setLoading(false);
      return;
    }

    // Get connected user IDs (candidates)
    const connectedUserIds = vouchConns.map(vc =>
      vc.user_a_id === userId ? vc.user_b_id : vc.user_a_id
    );

    // Get job interests from connected candidates for jobs where HMs are also connected
    // First get HMs this voucher knows
    const { data: hmConns } = await supabase
      .from('vouch_connections')
      .select('user_a_id, user_b_id')
      .or(`user_a_id.eq.${userId},user_b_id.eq.${userId}`);

    const hmIds = (hmConns || []).map(vc =>
      vc.user_a_id === userId ? vc.user_b_id : vc.user_a_id
    );

    // Get jobs by those HMs
    const { data: hmJobs } = await supabase
      .from('jobs')
      .select('id')
      .in('hiring_manager_id', hmIds)
      .eq('is_active', true);

    setPotentialJobsCount(hmJobs ? hmJobs.length : 0);

    if (!hmJobs || hmJobs.length === 0) {
      setLoading(false);
      return;
    }

    const jobIds = hmJobs.map(j => j.id);

    // Get interests from connected candidates for those jobs with status='interested'
    const { data: pending } = await supabase
      .from('job_interests')
      .select(`
        *,
        jobs:job_id (*),
        candidate:candidate_id (id, display_name, photo_url, headline, current_company, current_title)
      `)
      .in('candidate_id', connectedUserIds)
      .in('job_id', jobIds)
      .eq('status', 'interested');

    if (pending) setPendingRequests(patchJoinPhotos(pending));
    setLoading(false);
  }, [userId]);

  // Fetch vouches this user has created
  const fetchMyVouches = useCallback(async () => {
    if (!userId) return;
    const { data } = await supabase
      .from('vouches')
      .select(`
        *,
        jobs:job_id (*, hiring_manager:hiring_manager_id(id, display_name, photo_url)),
        candidate:candidate_id (id, display_name, photo_url, headline),
        job_interest:job_interest_id (status, outcome, outcome_at)
      `)
      .eq('voucher_id', userId)
      .order('created_at', { ascending: false });
    if (data) setMyVouches(patchJoinPhotos(data));
  }, [userId]);

  useEffect(() => {
    fetchPendingRequests();
    fetchMyVouches();
  }, [fetchPendingRequests, fetchMyVouches]);

  async function approveVouch(jobInterestId, candidateId, jobId, note) {
    if (!user) return;

    // Create the vouch
    const { error: vouchError } = await supabase
      .from('vouches')
      .insert({
        job_interest_id: jobInterestId,
        voucher_id: user.id,
        candidate_id: candidateId,
        job_id: jobId,
        note,
      });

    if (vouchError) return { error: vouchError };

    // Update interest status to 'vouched'
    const { error: updateError } = await supabase
      .from('job_interests')
      .update({ status: 'vouched', updated_at: new Date().toISOString() })
      .eq('id', jobInterestId);

    if (!updateError) {
      await fetchPendingRequests();
      await fetchMyVouches();
    }
    return { error: updateError };
  }

  async function rejectVouch(jobInterestId, reason, candidateId) {
    if (!user) return;
    const { error } = await supabase
      .from('job_interests')
      .update({ status: 'rejected', updated_at: new Date().toISOString() })
      .eq('id', jobInterestId);
    if (!error) {
      // Send decline reason as a message to the candidate
      if (candidateId && reason) {
        await supabase.from('messages').insert({
          sender_id: user.id,
          recipient_id: candidateId,
          job_interest_id: jobInterestId,
          content: reason,
        });
      }
      await fetchPendingRequests();
    }
    return { error };
  }

  // Subscribe to new job interests from candidates in network
  function subscribeToRequests(callback) {
    if (!user) return () => {};
    const channelName = `voucher-interests-${Date.now()}`;
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'job_interests',
      }, (payload) => {
        callback(payload);
        fetchPendingRequests();
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'job_interests',
      }, (payload) => {
        callback(payload);
        fetchPendingRequests();
        fetchMyVouches();
      })
      .subscribe((status, err) => {
        if (err && err.message?.includes('aborted')) return;
      });

    return () => {
      try { supabase.removeChannel(channel); } catch (_) {}
    };
  }

  return {
    pendingRequests,
    myVouches,
    potentialJobsCount,
    loading,
    fetchPendingRequests,
    fetchMyVouches,
    approveVouch,
    rejectVouch,
    subscribeToRequests,
  };
}
